import React, { useEffect, useState } from 'react';
import { expenseInvoicesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  ChevronLeft, ChevronRight, Loader2, FileText, Folder, Link2, Unlink,
  ExternalLink, AlertTriangle, CheckCircle2, Circle, Sparkles, Check,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtEur = (v) => `${Number(v || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const monthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
};
const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const ExpenseInvoicesCard = () => {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // navegador de archivos para vincular
  const [pickTx, setPickTx] = useState(null);
  const [folderStack, setFolderStack] = useState([]); // [{id, name}]
  const [files, setFiles] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);

  const load = async (m = month) => {
    setLoading(true);
    try {
      const res = await expenseInvoicesAPI.status(m);
      setData(res);
    } catch (e) {
      toast.error(e.message || 'No se pudo cargar el estado de facturas');
    }
    setLoading(false);
  };

  useEffect(() => { load(month); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const shiftMonth = (dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const openPicker = async (exp) => {
    setPickTx(exp);
    setFolderStack([]);
    await browseFolder(null);
  };

  const browseFolder = async (folderId) => {
    setLoadingFiles(true);
    try {
      const res = await expenseInvoicesAPI.files(folderId || undefined);
      setFiles(res);
    } catch (e) {
      toast.error(e.message || 'No se pudo leer la carpeta');
    }
    setLoadingFiles(false);
  };

  const enterFolder = (item) => {
    setFolderStack((prev) => [...prev, { id: item.id, name: item.name }]);
    browseFolder(item.id);
  };

  const goBack = () => {
    const next = folderStack.slice(0, -1);
    setFolderStack(next);
    browseFolder(next.length ? next[next.length - 1].id : null);
  };

  const linkFile = async (item) => {
    try {
      await expenseInvoicesAPI.link({
        tx_key: pickTx.tx_key, file_id: item.id, file_name: item.name, file_url: item.url,
      });
      toast.success('Factura vinculada');
      setPickTx(null);
      load(month);
    } catch (e) { toast.error(e.message || 'No se pudo vincular'); }
  };

  const unlink = async (exp) => {
    try {
      await expenseInvoicesAPI.unlink(exp.tx_key);
      toast.success('Factura desvinculada');
      load(month);
    } catch (e) { toast.error(e.message); }
  };

  const acceptSuggestion = async (exp) => {
    try {
      await expenseInvoicesAPI.link({
        tx_key: exp.tx_key,
        file_id: exp.suggested.file_id,
        file_name: exp.suggested.file_name,
        file_url: exp.suggested.file_url,
      });
      toast.success('Factura vinculada (sugerencia aceptada)');
      load(month);
    } catch (e) { toast.error(e.message || 'No se pudo vincular'); }
  };

  return (
    <Card data-testid="expense-invoices-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" /> Facturas de gasto (Drive)
            </CardTitle>
            <CardDescription>Vincula cada cargo del banco con su factura de la carpeta de Drive para la asesoría.</CardDescription>
          </div>
          <div className="flex items-center gap-1 border rounded-lg bg-background px-1 py-0.5">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shiftMonth(-1)} data-testid="inv-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium capitalize min-w-[120px] text-center" data-testid="inv-month-label">{monthLabel(month)}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shiftMonth(1)} data-testid="inv-next-btn"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : !data ? null : (
          <>
            {!data.folder_ok && (
              <div className="mx-4 mb-3 p-3 rounded-lg border border-amber-300 bg-amber-50 text-sm text-amber-800 flex items-start gap-2" data-testid="inv-folder-warning">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  <strong>La carpeta de Drive no es accesible.</strong> Entra en Drive → clic derecho en la carpeta de facturas → Compartir → <em>"Cualquier persona con el enlace: Lector"</em>. En cuanto lo hagas, aquí aparecerán tus facturas automáticamente.
                </span>
              </div>
            )}
            <div className="px-4 pb-2 flex items-center gap-2 text-sm">
              <Badge variant="outline" data-testid="inv-summary">
                {data.linked_count} de {data.total} gastos con factura
              </Badge>
              {data.total - data.linked_count > 0 && (
                <span className="text-xs text-amber-700">{data.total - data.linked_count} sin factura — no te olvides de mandarlas a la asesoría</span>
              )}
            </div>
            <div className="divide-y max-h-[380px] overflow-y-auto">
              {data.expenses.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">Sin gastos en el banco este mes.</p>
              )}
              {data.expenses.map((e) => (
                <div key={e.tx_key} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm" data-testid={`inv-expense-${e.tx_key}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {e.linked
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      : <Circle className="w-4 h-4 text-amber-500 shrink-0" />}
                    <span className="text-xs text-muted-foreground w-20 shrink-0">{(e.date || '').slice(0, 10)}</span>
                    <span className="truncate" title={e.description}>{e.description}</span>
                    {e.category && <Badge variant="outline" className="text-[9px] shrink-0 hidden md:inline-flex">{e.category}</Badge>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-semibold text-red-600">-{fmtEur(e.amount)}</span>
                    {e.linked ? (
                      <>
                        <a href={e.linked.file_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline max-w-[160px] truncate"
                          data-testid={`inv-linked-file-${e.tx_key}`}>
                          <FileText className="w-3 h-3 shrink-0" /><span className="truncate">{e.linked.file_name || 'Factura'}</span><ExternalLink className="w-3 h-3 shrink-0" />
                        </a>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" title="Desvincular" onClick={() => unlink(e)} data-testid={`inv-unlink-${e.tx_key}`}>
                          <Unlink className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        {e.suggested && (
                          <span className="inline-flex items-center gap-1 text-xs bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-full pl-2 pr-1 py-0.5 max-w-[220px]" data-testid={`inv-suggested-${e.tx_key}`}>
                            <Sparkles className="w-3 h-3 shrink-0" />
                            <a href={e.suggested.file_url} target="_blank" rel="noreferrer" className="truncate hover:underline" title={`Sugerencia: ${e.suggested.file_name}`}>
                              {e.suggested.file_name}
                            </a>
                            <button
                              className="shrink-0 rounded-full bg-indigo-600 text-white w-4 h-4 inline-flex items-center justify-center hover:bg-indigo-700"
                              title="Aceptar sugerencia y vincular"
                              onClick={() => acceptSuggestion(e)}
                              data-testid={`inv-accept-suggestion-${e.tx_key}`}
                            >
                              <Check className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        )}
                        <Button variant="outline" size="sm" className="h-7 text-xs" disabled={!data.folder_ok}
                          onClick={() => openPicker(e)} data-testid={`inv-link-btn-${e.tx_key}`}>
                          <Link2 className="w-3 h-3 mr-1" /> Vincular
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>

      {/* Navegador de archivos de Drive */}
      <Dialog open={!!pickTx} onOpenChange={(o) => !o && setPickTx(null)}>
        <DialogContent className="max-w-md" data-testid="inv-picker-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Elegir factura · {pickTx && `-${fmtEur(pickTx.amount)}`}</DialogTitle>
            <p className="text-xs text-muted-foreground truncate">{pickTx?.description}</p>
          </DialogHeader>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {folderStack.length > 0 && (
              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={goBack} data-testid="inv-picker-back">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Atrás
              </Button>
            )}
            <span className="truncate">/ {folderStack.map((f) => f.name).join(' / ') || 'Carpeta de facturas'}</span>
          </div>
          <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
            {loadingFiles ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
            ) : (files?.items || []).length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Carpeta vacía o no accesible.</p>
            ) : (
              (files.items || []).map((it) => (
                <button key={it.id}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-muted/50 transition-colors"
                  onClick={() => (it.is_folder ? enterFolder(it) : linkFile(it))}
                  data-testid={`inv-picker-item-${it.id}`}>
                  {it.is_folder ? <Folder className="w-4 h-4 text-amber-500 shrink-0" /> : <FileText className="w-4 h-4 text-primary shrink-0" />}
                  <span className="truncate flex-1">{it.name}</span>
                  {!it.is_folder && <Link2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ExpenseInvoicesCard;
